import { lazy, Suspense, useEffect, useId, useRef, useState } from "react";
import { Download, Eye, Paperclip, Replace, X } from "lucide-react";
import { ErrorBoundary } from "react-error-boundary";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  mailAttachmentSize,
  validateMailAttachments,
  type MailAttachment,
} from "../../../../supabase/functions/_shared/mailAttachments";
import {
  attachmentBlob,
  filesToPayload,
  formatMailFileSize,
} from "./mailFiles";

const PdfAttachmentPreview = lazy(() => import("./PdfAttachmentPreview"));

export const MailAttachments = ({
  value = [],
  onChange,
  disabled,
  onBusyChange,
  label = "Вложения",
}: {
  value?: MailAttachment[];
  onChange?: (files: MailAttachment[]) => void;
  disabled?: boolean;
  onBusyChange?: (busy: boolean) => void;
  label?: string;
}) => {
  const id = useId();
  const replacementInput = useRef<HTMLInputElement>(null);
  const replacing = useRef<number | undefined>(undefined);
  const reading = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [viewing, setViewing] = useState<MailAttachment>();
  const change = async (files: File[], index?: number) => {
    if (!files.length || reading.current) return;
    reading.current = true;
    setBusy(true);
    onBusyChange?.(true);
    setError("");
    try {
      const added = await filesToPayload(files);
      const next =
        index == null
          ? [...value, ...added]
          : value.map((file, i) => (i === index ? added[0] : file));
      onChange?.(validateMailAttachments(next));
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Не удалось прочитать файл",
      );
    } finally {
      reading.current = false;
      setBusy(false);
      onBusyChange?.(false);
    }
  };
  const download = (file: MailAttachment) => {
    const url = URL.createObjectURL(
      attachmentBlob(file, "application/octet-stream"),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = file.filename;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <section aria-label={label} className="min-w-0 space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Label htmlFor={onChange ? id : undefined}>{label}</Label>
        <span className="text-xs text-muted-foreground">
          {value.length} / 10 ·{" "}
          {formatMailFileSize(
            value.reduce((sum, file) => sum + mailAttachmentSize(file), 0),
          )}{" "}
          / 15 МБ
        </span>
      </div>
      {onChange && (
        <>
          <Input
            id={id}
            type="file"
            multiple
            disabled={disabled || busy}
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              e.target.value = "";
              void change(files);
            }}
          />
          <input
            aria-label="Файл для замены вложения"
            className="hidden"
            type="file"
            ref={replacementInput}
            disabled={disabled || busy}
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              e.target.value = "";
              void change(files, replacing.current);
            }}
          />
        </>
      )}
      {busy && (
        <p role="status" className="text-sm">
          Читаем файлы…
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {!value.length && (
        <p className="text-sm text-muted-foreground">Файлы не прикреплены.</p>
      )}
      <ul className="space-y-2">
        {value.map((file, index) => (
          <li
            key={index}
            className="flex min-w-0 flex-wrap items-center gap-2 rounded-md border p-2"
          >
            <Paperclip className="size-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1 basis-32">
              <p className="break-all text-sm">{file.filename}</p>
              <p className="text-xs text-muted-foreground">
                {formatMailFileSize(mailAttachmentSize(file))}
              </p>
            </div>
            <div className="flex flex-wrap gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Посмотреть ${file.filename}`}
                onClick={() => setViewing(file)}
              >
                <Eye />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Скачать ${file.filename}`}
                onClick={() => download(file)}
              >
                <Download />
              </Button>
              {onChange && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={disabled || busy}
                    aria-label={`Заменить ${file.filename}`}
                    onClick={() => {
                      replacing.current = index;
                      replacementInput.current?.click();
                    }}
                  >
                    <Replace />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={disabled || busy}
                    aria-label={`Убрать ${file.filename}`}
                    onClick={() =>
                      onChange(value.filter((_, i) => i !== index))
                    }
                  >
                    <X />
                  </Button>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
      {viewing && (
        <AttachmentPreview
          file={viewing}
          onClose={() => setViewing(undefined)}
          onDownload={() => download(viewing)}
        />
      )}
    </section>
  );
};

const AttachmentPreview = ({
  file,
  onClose,
  onDownload,
}: {
  file: MailAttachment;
  onClose: () => void;
  onDownload: () => void;
}) => {
  const [url, setUrl] = useState("");
  useEffect(() => {
    const next = URL.createObjectURL(attachmentBlob(file));
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  const image = /^image\/(png|jpeg|gif|webp)$/.test(file.contentType);
  const pdf = file.contentType === "application/pdf";
  const text =
    file.contentType === "text/plain" && mailAttachmentSize(file) <= 200_000;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="max-h-[90dvh] overflow-y-auto sm:max-w-4xl"
        onEscapeKeyDown={(event) => event.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="break-all pr-5">{file.filename}</DialogTitle>
          <DialogDescription>
            {formatMailFileSize(mailAttachmentSize(file))}
          </DialogDescription>
        </DialogHeader>
        {url &&
          (image ? (
            <img
              className="max-h-[65dvh] w-full object-contain"
              src={url}
              alt={file.filename}
            />
          ) : pdf ? (
            <ErrorBoundary
              fallback={
                <p role="alert">
                  Не удалось открыть просмотр PDF. Скачайте файл.
                </p>
              }
            >
              <Suspense fallback={<p role="status">Загрузка просмотра PDF…</p>}>
                <PdfAttachmentPreview file={file} />
              </Suspense>
            </ErrorBoundary>
          ) : text ? (
            <pre className="max-h-[60dvh] overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-4 text-sm">
              {new TextDecoder().decode(
                Uint8Array.from(atob(file.contentBase64), (c) =>
                  c.charCodeAt(0),
                ),
              )}
            </pre>
          ) : (
            <p className="text-sm">
              Этот формат можно скачать и открыть в подходящей программе.
            </p>
          ))}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Закрыть просмотр
          </Button>
          <Button type="button" onClick={onDownload}>
            <Download />
            Скачать файл
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
